import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import { authApi } from '@/api/auth'
import type { RegisterData } from '@/types'

const registerSchema = z.object({
  email: z.string().email('Email non valida'),
  password: z.string()
    .min(8, 'La password deve essere almeno 8 caratteri')
    .regex(/[A-Z]/, 'La password deve contenere almeno una lettera maiuscola')
    .regex(/[a-z]/, 'La password deve contenere almeno una lettera minuscola')
    .regex(/[0-9]/, 'La password deve contenere almeno un numero'),
  password_confirm: z.string().min(1, 'Conferma password richiesta'),
  first_name: z.string().min(1, 'Nome richiesto').max(100, 'Nome troppo lungo'),
  last_name: z.string().min(1, 'Cognome richiesto').max(100, 'Cognome troppo lungo'),
  company: z.string().optional(),
  job_title: z.string().optional(),
  phone: z.string().optional(),
  privacy_accepted: z.literal(true, {
    errorMap: () => ({ message: 'Devi accettare la privacy policy' }),
  }),
  marketing_consent: z.boolean().optional(),
}).refine((data) => data.password === data.password_confirm, {
  message: 'Le password non corrispondono',
  path: ['password_confirm'],
})

type RegisterForm = z.infer<typeof registerSchema>

export default function RegisterPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [registrationSuccess, setRegistrationSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      privacy_accepted: false as unknown as true,
      marketing_consent: false,
    },
  })

  const onSubmit = async (data: RegisterForm) => {
    setIsLoading(true)
    try {
      const registerData: RegisterData = {
        email: data.email,
        password: data.password,
        password_confirm: data.password_confirm,
        first_name: data.first_name,
        last_name: data.last_name,
        company: data.company,
        job_title: data.job_title,
        phone: data.phone,
        privacy_accepted: data.privacy_accepted,
        marketing_consent: data.marketing_consent,
      }

      await authApi.register(registerData)
      setRegistrationSuccess(true)
      toast.success('Registrazione completata!')
    } catch (error: any) {
      const errorData = error.response?.data
      if (errorData) {
        // Handle field-specific errors
        if (typeof errorData === 'object') {
          Object.entries(errorData).forEach(([key, value]) => {
            const message = Array.isArray(value) ? value[0] : value
            toast.error(`${key}: ${message}`)
          })
        } else {
          toast.error(errorData.detail || 'Errore durante la registrazione')
        }
      } else {
        toast.error('Errore durante la registrazione')
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (registrationSuccess) {
    return (
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
          <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Registrazione completata!</h2>
        <p className="mt-4 text-gray-600">
          La tua richiesta di registrazione è stata inviata con successo.
        </p>
        <p className="mt-2 text-gray-600">
          Un amministratore esaminerà la tua richiesta e riceverai una notifica
          non appena il tuo account sarà attivato.
        </p>
        <div className="mt-6">
          <Link
            to="/login"
            className="btn-primary inline-flex items-center"
          >
            Torna al Login
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 text-center">Registrazione</h2>
      <p className="mt-2 text-sm text-gray-600 text-center">
        Crea un nuovo account per accedere alla community
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5">
        {/* Email */}
        <div>
          <label htmlFor="email" className="label">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            {...register('email')}
            className="input"
            placeholder="nome@azienda.com"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        {/* Nome e Cognome */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="first_name" className="label">
              Nome <span className="text-red-500">*</span>
            </label>
            <input
              id="first_name"
              type="text"
              autoComplete="given-name"
              {...register('first_name')}
              className="input"
              placeholder="Mario"
            />
            {errors.first_name && (
              <p className="mt-1 text-sm text-red-600">{errors.first_name.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="last_name" className="label">
              Cognome <span className="text-red-500">*</span>
            </label>
            <input
              id="last_name"
              type="text"
              autoComplete="family-name"
              {...register('last_name')}
              className="input"
              placeholder="Rossi"
            />
            {errors.last_name && (
              <p className="mt-1 text-sm text-red-600">{errors.last_name.message}</p>
            )}
          </div>
        </div>

        {/* Azienda e Ruolo */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="company" className="label">
              Azienda
            </label>
            <input
              id="company"
              type="text"
              autoComplete="organization"
              {...register('company')}
              className="input"
              placeholder="Nome Azienda S.r.l."
            />
          </div>
          <div>
            <label htmlFor="job_title" className="label">
              Ruolo
            </label>
            <input
              id="job_title"
              type="text"
              autoComplete="organization-title"
              {...register('job_title')}
              className="input"
              placeholder="DPO, Compliance Manager..."
            />
          </div>
        </div>

        {/* Telefono */}
        <div>
          <label htmlFor="phone" className="label">
            Telefono
          </label>
          <input
            id="phone"
            type="tel"
            autoComplete="tel"
            {...register('phone')}
            className="input"
            placeholder="+39 123 456 7890"
          />
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="label">
            Password <span className="text-red-500">*</span>
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            {...register('password')}
            className="input"
          />
          {errors.password && (
            <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Minimo 8 caratteri, almeno una maiuscola, una minuscola e un numero
          </p>
        </div>

        {/* Conferma Password */}
        <div>
          <label htmlFor="password_confirm" className="label">
            Conferma Password <span className="text-red-500">*</span>
          </label>
          <input
            id="password_confirm"
            type="password"
            autoComplete="new-password"
            {...register('password_confirm')}
            className="input"
          />
          {errors.password_confirm && (
            <p className="mt-1 text-sm text-red-600">{errors.password_confirm.message}</p>
          )}
        </div>

        {/* Privacy Policy */}
        <div className="space-y-3">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="privacy_accepted"
                type="checkbox"
                {...register('privacy_accepted')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="privacy_accepted" className="text-gray-700">
                Ho letto e accetto la{' '}
                <a
                  href="https://www.conquist.it/amly/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-500"
                >
                  Privacy Policy
                </a>{' '}
                e i{' '}
                <a
                  href="https://www.conquist.it/amly/termini-e-condizioni/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-500"
                >
                  Termini di Servizio
                </a>{' '}
                <span className="text-red-500">*</span>
              </label>
            </div>
          </div>
          {errors.privacy_accepted && (
            <p className="text-sm text-red-600">{errors.privacy_accepted.message}</p>
          )}

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="marketing_consent"
                type="checkbox"
                {...register('marketing_consent')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="marketing_consent" className="text-gray-700">
                Acconsento a ricevere comunicazioni informative e promozionali
              </label>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full"
        >
          {isLoading ? 'Registrazione in corso...' : 'Registrati'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600">
        Hai gia un account?{' '}
        <Link to="/login" className="text-primary-600 hover:text-primary-500 font-medium">
          Accedi
        </Link>
      </p>
    </div>
  )
}
